import type { StateCreator } from 'zustand';

/**
 * Auth slice — tracks the currently signed-in Google session.
 *
 * The global store at `src/state/store.ts` doesn't exist yet (it's added in a
 * later wave). This module exports a zustand *slice creator* so it can be
 * composed in once the store lands.
 */

export interface AuthSliceState {
  /** Email address of the signed-in user, or `null` when signed out. */
  email: string | null;
  /** Display name from Google profile (e.g. 「曾英誠」). `null` until the
   *  userinfo fetch resolves or when the `profile` scope was denied. */
  name: string | null;
  /** Avatar URL from Google profile. `null` when not available. */
  picture: string | null;
  /** Current OAuth access token, or `null` when signed out. */
  token: string | null;
  /** Epoch milliseconds when the token expires, or `null` if unknown. */
  expiresAt: number | null;
  /** Epoch milliseconds when the user signed in this session. */
  loginAt: number | null;
}

export interface AuthSliceActions {
  /** Set the full session — typically called after a successful GIS sign-in. */
  setSession: (input: {
    token: string;
    email: string | null;
    expiresAt: number | null;
  }) => void;
  /** Patch the profile fields after the userinfo fetch completes. Kept
   *  separate from `setSession` because the userinfo round-trip is
   *  async and we don't want to block the bootstrap pipeline on it. */
  setProfile: (input: { name: string | null; picture: string | null; email?: string | null }) => void;
  /** Clear all session fields back to `null`. Does not call the GIS revoke endpoint. */
  clearSession: () => void;
}

export type AuthSlice = AuthSliceState & AuthSliceActions;

export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (set) => ({
  email: null,
  name: null,
  picture: null,
  token: null,
  expiresAt: null,
  loginAt: null,

  setSession: ({ token, email, expiresAt }) =>
    set({
      token,
      email,
      expiresAt,
      loginAt: Date.now(),
    }),

  setProfile: ({ name, picture, email }) =>
    set((state) => ({
      name,
      picture,
      // Backfill email from userinfo when login flow didn't have it
      // (GIS access-token responses don't include email — userinfo does).
      email: email ?? state.email,
    })),

  clearSession: () =>
    set({
      email: null,
      name: null,
      picture: null,
      token: null,
      expiresAt: null,
      loginAt: null,
    }),
});
